import { useEffect, useRef } from 'react';
import { NodeViewWrapper } from '@tiptap/react';

const ResizableVideo = ({ node, updateAttributes }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startWidth = useRef(0);
  const startHeight = useRef(0);

  // Media sync: teacher dispatches play/pause events; student listens and mirrors
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const src = node.attrs.src;

    const onPlay  = () => window.dispatchEvent(new CustomEvent('docMediaPublish', { detail: { src, action: 'play',  currentTime: video.currentTime } }));
    const onPause = () => window.dispatchEvent(new CustomEvent('docMediaPublish', { detail: { src, action: 'pause', currentTime: video.currentTime } }));
    video.addEventListener('play',  onPlay);
    video.addEventListener('pause', onPause);

    const onSync = (e) => {
      const { src: eSrc, action, currentTime } = e.detail || {};
      if (eSrc !== src) return;
      if (typeof currentTime === 'number') video.currentTime = currentTime;
      if (action === 'play')  video.play().catch(() => {});
      else if (action === 'pause') video.pause();
    };
    window.addEventListener('docMediaSync', onSync);

    return () => {
      video.removeEventListener('play',  onPlay);
      video.removeEventListener('pause', onPause);
      window.removeEventListener('docMediaSync', onSync);
    };
  }, [node.attrs.src]);

  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    const handleMouseDown = (e) => {
      const isResizeHandle = (e.target.classList && e.target.classList.contains('resize-handle')) ||
        (typeof e.target.closest === 'function' && !!e.target.closest('.resize-handle'));
      if (isResizeHandle) {
        e.preventDefault();
        e.stopPropagation();
        isResizing.current = true;
        startX.current = e.clientX;
        startY.current = e.clientY;
        startWidth.current = video.offsetWidth;
        startHeight.current = video.offsetHeight;
      }
    };

    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      e.preventDefault();
      const deltaX = e.clientX - startX.current;
      const deltaY = e.clientY - startY.current;
      const newWidth = Math.max(120, startWidth.current + deltaX);
      const newHeight = Math.max(80, startHeight.current + deltaY);
      updateAttributes({ width: `${newWidth}px`, height: `${newHeight}px` });
    };

    const handleMouseUp = () => {
      isResizing.current = false;
    };

    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [updateAttributes]);

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 0);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 1);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  return (
    <NodeViewWrapper 
      style={{
        margin: '0',
        padding: '0',
        display: 'block'
      }}
      data-type="video"
      draggable
    >
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div 
        className="resizable-video-container" 
        ref={containerRef}
        style={{
          margin: '0',
          padding: '0',
          display: 'inline-block',
          position: 'relative',
          cursor: 'grab'
        }}
      >
        
        <video
          ref={videoRef}
          src={node.attrs.src}
          width={node.attrs.width}
          height={node.attrs.height}
          controls={node.attrs.controls}
          className="resizable-video"
          draggable={false}
          style={{
            margin: '0',
            padding: '0',
            display: 'block',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}
        />
        <div 
          className="resize-handle"
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-25px',
            width: '18px',
            height: '18px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)',
            cursor: 'se-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(0,0,0,0.7)',
            fontSize: '11px',
            fontWeight: 'bold',
            zIndex: 10,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.4) 100%)';
            el.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget;
            el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)';
            el.style.transform = 'scale(1)';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style={{ transform: 'scaleX(-1)', transformOrigin: '50% 50%', pointerEvents: 'none' }}>
            <path fillRule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707"/>
          </svg>
        </div>
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default ResizableVideo; 